import { API_ROUTES, type ApiResponse } from '@mangelapp/shared';
import { AuthResponse, Project, Ticket } from '@/types/api';

class ApiClient {
  private baseUrl: string;
  private accessToken: string | null = null;

  constructor(baseUrl: string = '') {
    this.baseUrl = baseUrl || import.meta.env.VITE_API_URL || 'https://api.folw.eu';
    // Load token from localStorage on initialization
    this.accessToken = localStorage.getItem('accessToken');
    // console.log('🔍 API Client constructor - Loaded token from localStorage:', this.accessToken ? this.accessToken.substring(0, 50) + '...' : 'null');
  }

  // Set the access token
  setAccessToken(token: string | null) {
    // console.log('🔑 Setting access token:', token ? token.substring(0, 50) + '...' : 'null');
    this.accessToken = token;
    if (token) {
      localStorage.setItem('accessToken', token);
      // console.log('🔑 Access token stored in localStorage');
      // console.log('🔑 localStorage accessToken:', localStorage.getItem('accessToken')?.substring(0, 50) + '...');
    } else {
      localStorage.removeItem('accessToken');
      // console.log('🔑 Access token removed from localStorage');
    }
  }

  // Get the access token
  getAccessToken(): string | null {
    return this.accessToken;
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;
    
    // console.log('🔍 API Client request - URL:', url);
    // console.log('🔍 API Client request - Method:', options.method || 'GET');
    
    const isFormData = options.body instanceof FormData;
    let language = 'en';
    if (typeof window !== 'undefined') {
      const storedLang = localStorage.getItem('i18nextLng');
      if (storedLang) {
        language = storedLang.split('-')[0];
      } else if (navigator.language) {
        language = navigator.language.split('-')[0];
      }
    }
    const headers: Record<string, string> = {
      ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
      'Accept-Language': language,
      ...(options.headers as Record<string, string>),
    };

    // Add JWT token to headers if available
    const isLoginEndpoint = endpoint === '/api/auth/login';
    if (this.accessToken && !isLoginEndpoint) {
      headers['Authorization'] = `Bearer ${this.accessToken}`;
    }
    
    // console.log('🔍 API Client request - Headers:', headers);
    
    const config: RequestInit = {
      ...options,
      headers,
      credentials: 'include',
    };

    try {
      // console.log('🔍 API Client request - Making fetch request to:', url);
      // console.log('🔍 API Client request - Request body:', options.body);
      // console.log('🔍 API Client request - Headers:', headers);
      // console.log('🔍 API Client request - Config headers:', config.headers);
      // console.log('🔍 API Client request - Config body:', config.body);
      
      const response = await fetch(url, config);
      // console.log('🔍 API Client request - Response status:', response.status);
      // console.log('🔍 API Client request - Response ok:', response.ok);
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        // console.log('🔍 API Client request - Error data:', errorData);
        throw new ApiError(errorData.message || `HTTP ${response.status}`, response.status, errorData);
      }

      const data = await response.json();
      // console.log('🔍 API Client request - Response data:', data);
      return data;
    } catch (error) {
      console.error(`API request failed: ${endpoint}`, error);
      throw error;
    }
  }

  // Auth methods
  async login(email: string, password: string, language?: string) {
    const requestBody = { email, password, username: email } as any;
    // console.log('🔍 API Client - Sending login request:', requestBody);
    // console.log('🔍 API Client - Accept-Language:', language || 'en');
    
    const response = await this.request<AuthResponse>(API_ROUTES.AUTH.LOGIN, {
      method: 'POST',
      headers: {
        'Accept-Language': language || 'en',
      },
      body: JSON.stringify(requestBody),
    });
    
    // Store the access token
    if (response.accessToken) {
      this.setAccessToken(response.accessToken);
    }
    
    return response;
  }

  async logout() {
    // Clear the access token
    this.setAccessToken(null);
    
    // Call logout endpoint if needed
    try {
      return await this.request(API_ROUTES.AUTH.LOGOUT, {
        method: 'POST',
      });
    } catch (error) {
      // Even if logout fails, we've cleared the token locally
      console.log('Logout endpoint failed, but token cleared locally');
    }
  }

  async register(userData: { 
    username: string; 
    email: string; 
    password: string; 
    firstName: string; 
    lastName: string;
    displayName?: string;
    phone?: string;
    position?: string;
  }) {
    return this.request(API_ROUTES.AUTH.REGISTER, {
      method: 'POST',
      body: JSON.stringify(userData),
    });
  }

  async getMe(organizationId?: string): Promise<AuthResponse> {
    // console.log('🔍 API Client getMe - Access Token:', this.accessToken ? this.accessToken.substring(0, 50) + '...' : 'null');
    // console.log('🔍 API Client getMe - Base URL:', this.baseUrl);
    const url = organizationId ? `${API_ROUTES.AUTH.ME}?organizationId=${encodeURIComponent(organizationId)}` : API_ROUTES.AUTH.ME;
    // console.log('🔍 API Client getMe - Full URL:', `${this.baseUrl}${url}`);
    return this.request(url) as Promise<AuthResponse>;
  }

  async getCurrentProvisioningStatus(organizationId?: string): Promise<{
    success: boolean;
    data?: {
      organizationId: string;
      status: 'pending' | 'ready' | 'failed';
      error?: string | null;
    };
    message?: string;
  }> {
    const suffix = organizationId
      ? `?organizationId=${encodeURIComponent(organizationId)}`
      : '';
    return this.request(`/api/orgs/current/provisioning-status${suffix}`, {
      method: 'GET',
    });
  }

  async retryOrganizationProvisioning(organizationId: string): Promise<{
    success: boolean;
    data?: { started: boolean; status: 'pending' | 'ready' | 'failed' };
    message?: string;
  }> {
    return this.request(`/api/orgs/${organizationId}/provisioning/retry`, {
      method: 'POST',
    });
  }

  async changePassword(currentPassword: string, newPassword: string) {
    return this.request('/api/auth/change-password', {
      method: 'POST',
      body: JSON.stringify({ currentPassword, newPassword }),
    });
  }

  async deleteAccount(password: string, confirmation: string = 'DELETE') {
    return this.request<ApiResponse & { data?: { ownedOrganizationsArchived?: boolean } }>(
      '/api/auth/delete-account',
      {
        method: 'POST',
        body: JSON.stringify({ password, confirmation }),
      }
    );
  }

  async forgotPassword(email: string, language?: string) {
    return this.request<ApiResponse>(API_ROUTES.AUTH.FORGOT_PASSWORD, {
      method: 'POST',
      headers: {
        'Accept-Language': language || 'en',
      },
      body: JSON.stringify({ email }),
    });
  }

  async verifyResetToken(token: string) {
    return this.request<ApiResponse>(API_ROUTES.AUTH.VERIFY_RESET_TOKEN(token), {
      method: 'GET',
    });
  }

  async resetPassword(token: string, newPassword: string, language?: string) {
    return this.request<ApiResponse>(API_ROUTES.AUTH.RESET_PASSWORD, {
      method: 'POST',
      headers: {
        'Accept-Language': language || 'en',
      },
      body: JSON.stringify({ token, newPassword }),
    });
  }

  async verifyEmail(token: string) {
    return this.request<ApiResponse>(API_ROUTES.AUTH.VERIFY_EMAIL(token), {
      method: 'GET',
    });
  }

  async resendVerificationEmail(email: string, language?: string) {
    return this.request<ApiResponse>(API_ROUTES.AUTH.RESEND_VERIFICATION, {
      method: 'POST',
      headers: {
        'Accept-Language': language || 'en',
      },
      body: JSON.stringify({ email }),
    });
  }

  async getVerificationStatus() {
    return this.request<ApiResponse<{ emailVerified: boolean }>>(API_ROUTES.AUTH.VERIFICATION_STATUS, {
      method: 'GET',
    });
  }

  async updateProfile(profileData: any) {
    return this.request('/api/auth/update-profile', {
      method: 'POST',
      body: JSON.stringify(profileData),
    });
  }

  async updateSettings(settingsData: any) {
    return this.request('/api/auth/update-settings', {
      method: 'POST',
      body: JSON.stringify(settingsData),
    });
  }

  // Preferences methods
  async getPreferences(): Promise<ApiResponse<unknown>> {
    return this.request<ApiResponse<unknown>>('/api/preferences', {
      method: 'GET',
    });
  }

  async updatePreferences(preferences: any): Promise<ApiResponse<unknown>> {
    return this.request<ApiResponse<unknown>>('/api/preferences', {
      method: 'PUT',
      body: JSON.stringify(preferences),
    });
  }

  async testWhatsApp() {
    return this.request('/api/preferences/test-whatsapp', {
      method: 'POST',
    });
  }

  async upgradeSubscription(upgradeData: any) {
    return this.request('/api/subscriptions/upgrade', {
      method: 'POST',
      body: JSON.stringify(upgradeData),
    });
  }

  async createPaymentIntent(planId: string, billingInterval: 'monthly' | 'yearly' = 'monthly', seatCount: number) {
    const body: any = { planId, billingInterval, seatCount };
    return this.request('/api/stripe/create-payment-intent', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  async confirmSubscription(paymentIntentId: string, planId: string, billingInterval: 'monthly' | 'yearly' = 'monthly', seatCount: number) {
    const body: any = { paymentIntentId, planId, billingInterval, seatCount };
    return this.request('/api/stripe/confirm-subscription', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  async cancelSubscription() {
    return this.request('/api/stripe/cancel-subscription', {
      method: 'POST',
    });
  }

  async getDashboardColors(): Promise<
    ApiResponse<{
      items: Array<{ key: string; name: string; color: string; displayOrder: number }>;
      colors: Record<string, string>;
    }>
  > {
    return this.request(API_ROUTES.DASHBOARD_COLORS.LIST) as Promise<
      ApiResponse<{
        items: Array<{ key: string; name: string; color: string; displayOrder: number }>;
        colors: Record<string, string>;
      }>
    >;
  }

  // Projects methods
  async getProjects(): Promise<ApiResponse<Project[]>> {
    return this.request(API_ROUTES.PROJECTS.LIST) as Promise<ApiResponse<Project[]>>;
  }

  async getProject(id: string): Promise<ApiResponse<Project>> {
    return this.request(API_ROUTES.PROJECTS.GET(id)) as Promise<ApiResponse<Project>>;
  }

  async createProject(projectData: any) {
    return this.request(API_ROUTES.PROJECTS.CREATE, {
      method: 'POST',
      body: JSON.stringify(projectData),
    });
  }

  async updateProject(id: string, projectData: any) {
    return this.request(API_ROUTES.PROJECTS.UPDATE(id), {
      method: 'PUT',
      body: JSON.stringify(projectData),
    });
  }

  async patchProject(id: string, updates: any) {
    return this.request(API_ROUTES.PROJECTS.UPDATE(id), {
      method: 'PATCH',
      body: JSON.stringify(updates),
    });
  }

  async deleteProject(id: string) {
    return this.request(API_ROUTES.PROJECTS.DELETE(id), {
      method: 'DELETE',
    });
  }

  // Tickets methods
  async getTickets(): Promise<ApiResponse<Ticket[]>> {
    return this.request(API_ROUTES.TICKETS.LIST) as Promise<ApiResponse<Ticket[]>>;
  }

  async getTicketsByProject(projectId: string): Promise<ApiResponse<Ticket[]>> {
    return this.request(API_ROUTES.TICKETS.BY_PROJECT(projectId)) as Promise<ApiResponse<Ticket[]>>;
  }

  async getNextTicketNumber(projectId: string) {
    return this.request(`/api/tickets/next-number/${projectId}`);
  }

  async getTicket(id: string) {
    return this.request(API_ROUTES.TICKETS.GET(id));
  }

  async createTicket(ticketData: any) {
    return this.request(API_ROUTES.TICKETS.CREATE, {
      method: 'POST',
      body: JSON.stringify(ticketData),
    });
  }

  async updateTicket(id: string, ticketData: any) {
    return this.request(API_ROUTES.TICKETS.UPDATE(id), {
      method: 'PUT',
      body: JSON.stringify(ticketData),
    });
  }

  async patchTicket(id: string, updates: any) {
    return this.request(API_ROUTES.TICKETS.UPDATE(id), {
      method: 'PATCH',
      body: JSON.stringify(updates),
    });
  }

  async confirmTicketOvertime(ticketId: string) {
    return this.request<ApiResponse<Ticket>>(`/api/tickets/${ticketId}/confirm-overtime`, {
      method: 'PUT',
    });
  }

  async deleteTicket(id: string) {
    return this.request(API_ROUTES.TICKETS.DELETE(id), {
      method: 'DELETE',
    });
  }

  async likeTicket(id: string) {
    return this.request(`/api/tickets/${id}/like`, {
      method: 'POST',
    });
  }

  async getTicketImages(id: string) {
    return this.request(`/api/tickets/${id}/images`);
  }

  async deleteTicketImage(id: string, imageIndex: number) {
    return this.request(`/api/tickets/${id}/images/${imageIndex}`, {
      method: 'DELETE',
    });
  }

  async addTicketImage(id: string, imageUrl: string, weatherData?: any) {
    return this.request(`/api/tickets/${id}/images`, {
      method: 'POST',
      body: JSON.stringify({ imageUrl, weatherData }),
    });
  }

  async getTicketImageMetadata(
    id: string,
    params?: { limit?: number; offset?: number },
  ): Promise<{ items: any[]; pagination: { total: number; limit: number; offset: number; hasMore: boolean } | null }> {
    const queryParams = new URLSearchParams();
    if (typeof params?.limit === 'number') queryParams.set('limit', String(params.limit));
    if (typeof params?.offset === 'number') queryParams.set('offset', String(params.offset));
    const suffix = queryParams.toString() ? `?${queryParams.toString()}` : '';

    const response = await this.request<{ success: boolean; data: any[]; pagination?: { total: number; limit: number; offset: number; hasMore: boolean } }>(
      `/api/tickets/${id}/image-metadata${suffix}`,
    );

    return {
      items: response.data || [],
      pagination: response.pagination || null,
    };
  }

  async updateTicketImages(id: string, images: string[]) {
    return this.request(`/api/tickets/${id}/images`, {
      method: 'PUT',
      body: JSON.stringify({ images }),
    });
  }

  // Upload ticket image to GCS and return public URL
  async uploadTicketImageToStore(file: File | Blob, filename = `upload-${Date.now()}.jpg`) {
    const formData = new FormData();
    // Backend expects field name 'image'
    // If we get a Blob without a name, provide a filename
    const toFile = file instanceof File ? file : new File([file], filename, { type: (file as any)?.type || 'image/jpeg' });
    formData.append('image', toFile);

    // Do not set Content-Type; browser sets multipart boundary
    return this.request('/api/tickets/images/upload', {
      method: 'POST',
      headers: {},
      body: formData,
    });
  }

  // Chat methods
  async getTicketChat(
    ticketId: string,
    params?: { limit?: number; offset?: number },
  ): Promise<{
    messages: any[];
    unreadCount: number;
    pagination: { total: number; limit: number; offset: number; hasMore: boolean } | null;
  }> {
    const queryParams = new URLSearchParams();
    if (typeof params?.limit === 'number') queryParams.set('limit', String(params.limit));
    if (typeof params?.offset === 'number') queryParams.set('offset', String(params.offset));
    const suffix = queryParams.toString() ? `?${queryParams.toString()}` : '';

    const response = await this.request<{
      success: boolean;
      data: { messages: any[]; unreadCount: number };
      pagination?: { total: number; limit: number; offset: number; hasMore: boolean };
    }>(`/api/tickets/${ticketId}/chat${suffix}`);

    return {
      messages: response.data?.messages || [],
      unreadCount: response.data?.unreadCount || 0,
      pagination: response.pagination || null,
    };
  }

  async sendTicketMessage(ticketId: string, message: string) {
    const response = await this.request<{ success: boolean; data: any }>(`/api/tickets/${ticketId}/chat`, {
      method: 'POST',
      body: JSON.stringify({ message }),
    });
    return response.data;
  }

  async markTicketChatAsRead(ticketId: string): Promise<boolean> {
    const response = await this.request<{ success: boolean }>(`/api/tickets/${ticketId}/chat/read`, {
      method: 'POST',
    });
    return response.success;
  }

  // History methods
  async getTicketHistory(
    ticketId: string,
    params?: { limit?: number; offset?: number },
  ): Promise<{ items: any[]; pagination: { total: number; limit: number; offset: number; hasMore: boolean } | null }> {
    const queryParams = new URLSearchParams();
    if (typeof params?.limit === 'number') queryParams.set('limit', String(params.limit));
    if (typeof params?.offset === 'number') queryParams.set('offset', String(params.offset));
    const suffix = queryParams.toString() ? `?${queryParams.toString()}` : '';

    const response = await this.request<{ success: boolean; data: any[]; pagination?: { total: number; limit: number; offset: number; hasMore: boolean } }>(
      `/api/tickets/${ticketId}/history${suffix}`,
    );
    return {
      items: response.data || [],
      pagination: response.pagination || null,
    };
  }

  async uploadTicketImage(id: string, file: File) {
    const formData = new FormData();
    formData.append('file', file);
    
    return this.request(`/api/tickets/${id}/images`, {
      method: 'POST',
      body: formData,
    });
  }

  /**
   * Generate a ticket card image on demand.
   * Returns the GCS URL and filename of the generated image.
   * 
   * @param ticketId - The ID of the ticket to generate an image for
   * @returns Object with imageUrl and filename
   */
  async generateTicketCardImage(ticketId: string): Promise<{ imageUrl: string; filename: string }> {
    const response = await this.request<{ success: boolean; data: { imageUrl: string; filename: string }; message?: string }>(
      API_ROUTES.TICKET_CARD_IMAGE.GENERATE(ticketId),
      { method: 'POST' }
    );
    
    if (!response.success || !response.data) {
      throw new ApiError(response.message || 'Failed to generate ticket card image', 500, response);
    }
    
    return response.data;
  }

  // Upload methods
  async uploadImage(file: File) {
    const formData = new FormData();
    formData.append('file', file);

    return this.request(API_ROUTES.UPLOADS.IMAGE, {
      method: 'POST',
      headers: {}, // Don't set Content-Type for FormData
      body: formData,
    });
  }

  async uploadVoice(file: File) {
    const formData = new FormData();
    formData.append('voice', file);

    return this.request(API_ROUTES.UPLOADS.VOICE, {
      method: 'POST',
      headers: {}, // Don't set Content-Type for FormData
      body: formData,
    });
  }

  async uploadDocument(file: File) {
    const formData = new FormData();
    formData.append('document', file);

    return this.request(API_ROUTES.UPLOADS.DOCUMENT, {
      method: 'POST',
      headers: {}, // Don't set Content-Type for FormData
      body: formData,
    });
  }

  // AI methods
  async analyzeImage(imageBase64: string, ticketContext: any, userMessage?: string, conversationHistory?: any[]) {
    return this.request(API_ROUTES.AI.ANALYZE_IMAGE, {
      method: 'POST',
      body: JSON.stringify({
        imageBase64,
        ticketContext,
        userMessage,
        conversationHistory,
      }),
    });
  }

  /**
   * Analyze a ticket image using OpenAI Vision API
   * @param imageUrl - Public URL of the image (GCS URL)
   * @param ticketId - Ticket ID
   * @param ticketContext - Optional context about the ticket
   */
  async analyzeTicketImage(
    imageUrl: string,
    ticketId: string,
    ticketContext?: { title?: string; description?: string; projectName?: string; projectId?: string }
  ) {
    // Get current language from localStorage (set by i18n) or default to 'en'
    let language = 'en';
    if (typeof window !== 'undefined') {
      const storedLang = localStorage.getItem('i18nextLng');
      if (storedLang) {
        language = storedLang.split('-')[0]; // Extract base language (e.g., 'en' from 'en-US')
      } else if (navigator.language) {
        language = navigator.language.split('-')[0];
      }
    }

    return this.request<{
      success: boolean;
      data: {
        id: string;
        analysisText: string;
        detectedDefects: string[];
        safetyRisks: string[];
        remainingWork: string[];
        qualityIssues: string[];
        riskLevel: 'low' | 'medium' | 'high' | 'critical';
        defectSeverity?: 'minor' | 'moderate' | 'serious' | 'critical';
        priorityRecommendation?: string;
        confidence: number;
        userApproved: boolean;
        createdAt: string;
      };
    }>(API_ROUTES.AI.ANALYZE_IMAGE, {
      method: 'POST',
      headers: {
        'Accept-Language': language,
      },
      body: JSON.stringify({
        imageUrl,
        ticketId,
        ticketContext,
      }),
    });
  }

  /**
   * Save/update image analysis approval
   * @param analysisId - Analysis ID
   * @param approved - Whether user approved the analysis
   * @param description - Optional custom description from user
   * @param useForTicketDescription - Whether to use this description for ticket description
   * @param saveAsImageDescription - Whether to save this as image description
   */
  async saveImageAnalysis(
    analysisId: string,
    approved: boolean,
    description?: string
  ) {
    return this.request<{ success: boolean; data: any }>('/api/ai/image-analysis/approve', {
      method: 'POST',
      body: JSON.stringify({
        analysisId,
        approved,
        description,
      }),
    });
  }

  /**
   * Save/update image analysis approval with additional options
   * @param analysisId - Analysis ID
   * @param approved - Whether user approved the analysis
   * @param userDescription - Optional custom description from user
   * @param useForTicketDescription - Whether to use this description for ticket description
   * @param saveAsImageDescription - Whether to save this as image description
   * @param imageUrl - Optional image URL for better matching
   */
  async saveImageAnalysisApproval(
    analysisId: string,
    approved: boolean,
    userDescription?: string,
    useForTicketDescription?: boolean,
    saveAsImageDescription?: boolean,
    imageUrl?: string
  ) {
    return this.request<{ success: boolean; data: any }>('/api/ai/image-analysis/approve', {
      method: 'POST',
      body: JSON.stringify({
        analysisId,
        approved,
        description: userDescription,
        useForTicketDescription,
        saveAsImageDescription,
        imageUrl, // Pass imageUrl for better matching
      }),
    });
  }

  /**
   * Get image analysis for a specific ticket and image
   * @param ticketId - Ticket ID
   * @param imageUrl - Image URL
   */
  async getImageAnalysis(ticketId: string, imageUrl: string) {
    return this.request<{
      success: boolean;
      data: {
        id: string;
        analysisText: string;
        detectedDefects: string[];
        safetyRisks: string[];
        remainingWork: string[];
        qualityIssues: string[];
        riskLevel: string;
        defectSeverity?: string;
        priorityRecommendation?: string;
        confidence: number;
        userApproved: boolean;
      } | null;
    }>(`/api/ai/image-analysis?ticketId=${encodeURIComponent(ticketId)}&imageUrl=${encodeURIComponent(imageUrl)}`, {
      method: 'GET',
    });
  }

  async continueConversation(ticketContext: any, userMessage: string, conversationHistory?: any[]) {
    return this.request('/api/ai/continue-conversation', {
      method: 'POST',
      body: JSON.stringify({
        ticketContext,
        userMessage,
        conversationHistory,
      }),
    });
  }

  async transcribeVoice(audioBase64: string, language = 'de') {
    return this.request('/api/ai/transcribe-voice', {
      method: 'POST',
      body: JSON.stringify({
        audioBase64,
        language,
      }),
    });
  }

  // Generic request method for custom endpoints
  // Team Groups
  async getTeamGroupMembers(groupId: string) {
    return this.request(`/api/team-groups/${groupId}/members`);
  }

  async removeTeamGroupMember(groupId: string, memberId: string) {
    return this.request(`/api/team-groups/${groupId}/members/${memberId}`, {
      method: 'DELETE',
    });
  }

  // Documents
  async getDocumentToken(documentId: string) {
    return this.request(`/api/documents/${documentId}/token`);
  }

  async replaceDocument(documentId: string, file: File) {
    const formData = new FormData();
    formData.append('file', file);
    
    return this.request(`/api/documents/${documentId}/replace`, {
      method: 'POST',
      body: formData,
    });
  }

  async replaceDocumentWithUrl(documentId: string, uploadURL: string, fileSize: number) {
    return this.request(`/api/documents/${documentId}/replace`, {
      method: 'PUT',
      body: JSON.stringify({
        uploadURL: uploadURL,
        fileSize: fileSize
      }),
    });
  }

  async customRequest<T = any>(endpoint: string, options: RequestInit = {}): Promise<T> {
    return this.request<T>(endpoint, options);
  }

  // Team Management
  async createTeam(teamData: any) {
    return this.request('/api/contractors', {
      method: 'POST',
      body: JSON.stringify(teamData),
    });
  }

  async updateTeam(teamId: string, teamData: any, organizationId?: string) {
    const endpoint = organizationId
      ? `/api/contractors/${teamId}?organizationId=${encodeURIComponent(organizationId)}`
      : `/api/contractors/${teamId}`;
    return this.request(endpoint, {
      method: 'PUT',
      body: JSON.stringify(teamData),
    });
  }

  async deleteTeam(teamId: string, organizationId?: string) {
    const endpoint = organizationId
      ? `/api/contractors/${teamId}?organizationId=${encodeURIComponent(organizationId)}`
      : `/api/contractors/${teamId}`;
    return this.request(endpoint, {
      method: 'DELETE',
    });
  }

  // Device tracking methods
  async registerDevice(deviceInfo: {
    userAgent: string;
    deviceType: 'mobile' | 'desktop' | 'tablet';
    operatingSystem: string;
    browser: string;
    screenResolution: string;
    timezone: string;
    language: string;
    metadata: Record<string, any>;
    deviceFingerprint: string;
    accessType?: 'login' | 'api_call' | 'page_view';
    endpoint?: string;
  }) {
    return this.request<ApiResponse<{ deviceId: string; isNewDevice: boolean }>>('/api/device-tracking/register', {
      method: 'POST',
      body: JSON.stringify(deviceInfo),
    });
  }

  async getUserDevices() {
    return this.request<ApiResponse<any[]>>('/api/device-tracking/devices', {
      method: 'GET',
    });
  }

  async getAccessHistory(limit?: number, offset?: number, deviceId?: string) {
    const params = new URLSearchParams();
    if (limit) params.append('limit', limit.toString());
    if (offset) params.append('offset', offset.toString());
    if (deviceId) params.append('deviceId', deviceId);
    
    const queryString = params.toString();
    const url = `/api/device-tracking/access-history${queryString ? `?${queryString}` : ''}`;
    
    return this.request<ApiResponse<any[]>>(url, {
      method: 'GET',
    });
  }

  // Generic GET method for API routes
  async get<T = any>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'GET',
    });
  }

  async post<T = any>(endpoint: string, body?: unknown): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'POST',
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
  }

  async put<T = any>(endpoint: string, body?: unknown): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PUT',
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
  }

  async delete<T = any>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'DELETE',
    });
  }
}

// Custom error class for API errors
export class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public data?: any
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

// Debug: Log the environment variable
console.log('VITE_API_URL:', import.meta.env.VITE_API_URL);
export const apiClient = new ApiClient(import.meta.env.VITE_API_URL || '');
